'use client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { BaseFillIntelligentDuration } from '@/app/components/icons/BaseFillIntelligentDuration';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { StarIcon } from '@/app/components/icons/StarIcon';
import { ViduLogo2 } from '@/app/components/icons/ViduLogo2';
import { CouponBadge } from '@/app/components/shared/CouponBadge';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy/hooks/use-retention-strategy';
import { CURRENCY, REFERENCE_RIGHT_LIST, SITE } from '@/app/constants';
import { useSyncHeight } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubscriptionProductType } from '@/app/types';
import { cn, formatAmount, formatPrice, formatPriceDiscount, hasSelectedCoupon } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import {
  CREDITS_CONFIG_LIST,
  type IndividualPlanType,
  PLAN_COMMON_RIGHTS,
  PLAN_HIGHLIGHTED_RIGHTS,
  PLAN_TITLE_TYPE_MAP,
} from '../constants';
import { getCreditsDiscount, getCreditsProgress } from '../utils';
import { CreditsProgressBar } from './CreditsProgressBar';

export const PlanCard = ({
  product,
  renderSubscriptionButton,
  isDiscount,
  showPrice,
  monthlyEquivalentSaved = 0,
}: {
  product: SubscriptionProductType;
  renderSubscriptionButton?: (product: SubscriptionProductType) => React.ReactNode;
  isDiscount?: boolean;
  hideExpiryWarning?: boolean;
  showPrice?: boolean;
  /** 年付相比同 plan 月付节省的金额（cent），仅 yearly 周期有意义 */
  monthlyEquivalentSaved?: number;
}) => {
  const bonusCode = useStore((state) => state.bonusCode);

  const t = useTranslations();
  const locale = useLocale();
  useSyncHeight({ selector: '.plan-title-section' });
  useSyncHeight({ selector: '.price-section' });

  const { isEligible: isRetentionEligible } = useRetentionStrategy();

  if (!product) return null;
  // team_v2 走独立的 TeamPlanCard，本组件断言 plan_type 不会是 team_v2。
  const planType = product.plan_type as IndividualPlanType;
  const { extra_grant_credit = 0 } = product;
  const monthsInPeriod = product.billing_period === 'yearly' ? 12 : 1;

  const showRetentionDiscount =
    isRetentionEligible && product.product_type === 'subscription' && product.billing_period === 'monthly';

  const renderDiscountBadge = (): ReactNode => {
    if (product.discount > 0) {
      const hasCoupon = hasSelectedCoupon(product);

      if (hasCoupon) {
        return <CouponBadge discount={product.discount} />;
      }

      if (showRetentionDiscount) {
        return (
          <div className="rounded-8 border border-white/30 bg-gradient-to-r from-[#b2d7ff] to-[#ffe3da] px-2 py-1 font-bold text-system-black text-xs uppercase italic shadow-[inset_0px_0px_6px_0px_white]">
            限时{formatPriceDiscount(product.discount, 'zh')}折
          </div>
        );
      }

      return (
        <div className="rounded-8 bg-[linear-gradient(90deg,#FF7678_0%,#FF383B_100%)] px-2 py-1 font-bold text-white text-xs uppercase italic leading-[1.4]">
          {t('payments.plans.discount', {
            discount: formatPriceDiscount(product.discount, locale),
          })}
        </div>
      );
    }

    return null;
  };

  const creditsDiscount = getCreditsDiscount(planType, locale);
  const showYearlySave =
    product.billing_period === 'yearly' && monthlyEquivalentSaved > 0 && planType !== 'free' && !(product.discount > 0);
  const highlightedRights = PLAN_HIGHLIGHTED_RIGHTS[planType];
  const commonRights = PLAN_COMMON_RIGHTS[planType];

  const isUltimate = planType === 'ultimate_v2';

  return (
    <div
      className={cn(
        'relative flex h-full flex-col items-center justify-start overflow-hidden',
        isUltimate && 'rounded-16 bg-[#5765F2]',
      )}
    >
      <div className="flex h-11 shrink-0 items-center justify-center">
        {isUltimate && (
          <div className="flex items-center gap-1 font-medium text-sm text-white leading-5">
            <StarIcon className="size-[14px] shrink-0" />
            <span>{t('payments.plans.bestValue')}</span>
          </div>
        )}
      </div>

      <div
        className={cn(
          'relative flex h-full w-full flex-col justify-start overflow-hidden rounded-16 border-2 border-system-white16 px-4 py-6 text-system-white',
          planType === 'free' && 'bg-[#0A0D10]',
          planType === 'standard_v2' && 'bg-[#0E1115] shadow-[inset_0_0_100px_0_rgba(207,231,255,0.08)]',
          planType === 'premium_v2' &&
            'border-[rgba(250,241,200,0.16)] bg-[#12100D] shadow-[inset_0_0_100px_0_rgba(250,241,200,0.08)]',
          isUltimate && 'border-[#5765F2] bg-[linear-gradient(180deg,#161722_0%,rgba(30,30,30,0.64)_100%)]',
        )}
      >
        {/* 套餐名称 */}
        <div className="plan-title-section border-system-white12 border-b pb-4 font-semibold text-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="flex flex-wrap items-center gap-2 bg-clip-text text-system-white text-transparent"
              style={PLAN_TITLE_TYPE_MAP[planType]}
            >
              {t(`payments.${planType}`)}
            </div>
            {isDiscount && renderDiscountBadge()}
          </div>

          {/* 套餐说明 */}
          {planType !== 'free' && (
            <div className="mt-1 font-normal text-system-white40 text-xs leading-[1.4]">
              {t(`payments.plans.planDescription.${planType}`)}
            </div>
          )}
        </div>

        {/* 价格 */}
        {(renderSubscriptionButton || showPrice) && (
          <div className="price-section py-6 text-sm text-system-white">
            <div className="flex items-end gap-1 leading-none">
              <span className="font-semibold text-2xl text-system-white40 leading-[1.4]">{CURRENCY}</span>
              <span className="font-semibold text-[48px] leading-none">
                {formatAmount(product.price / monthsInPeriod)}
              </span>
              <span className="text-base text-system-white16 leading-[1.4]">/</span>
              <span className="text-base text-system-white40 leading-[1.4]">{t('payments.per_monthly')}</span>
              {product.original_price && product.discount > 0 ? (
                <span className="text-base text-system-white40 leading-[1.4] line-through">
                  {formatPrice(product.original_price / monthsInPeriod)}
                </span>
              ) : null}
            </div>

            {product.billing_period === 'monthly' ? (
              product.product_type === 'subscription' && product.discount && product.original_price ? (
                <div className="mt-2 flex flex-col gap-2 text-sm text-system-white40">
                  <div>
                    {t('payments.plans.nextCycle', {
                      amount: formatPrice(product.original_price),
                    })}
                  </div>
                  <div className="flex flex-col gap-1">
                    {t.rich('payments.plans.discountDesc', {
                      br: () => <br />,
                    })}
                  </div>
                </div>
              ) : null
            ) : (
              <div className="mt-2 text-sm sm:hidden">
                {planType !== 'free' && (
                  <div className="text-system-white">
                    {t('payments.plans.billYearlyAs')}
                    {formatPrice(product.price)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 订阅按钮 */}
        {renderSubscriptionButton && (
          <div className="mb-5">
            {showYearlySave ? (
              <div className="rounded-12 bg-system-white08">
                {renderSubscriptionButton(product)}
                <div className="py-2 text-center text-system-white40 text-xs leading-5">
                  {t.rich('payments.plans.savingsVsMonthly', {
                    amount: () => <span className="text-system-white">{formatPrice(monthlyEquivalentSaved)}</span>,
                  })}
                </div>
              </div>
            ) : (
              renderSubscriptionButton(product)
            )}
          </div>
        )}

        {/* 积分权益 */}
        {planType !== 'free' && (
          <div className="relative mb-5 flex flex-col justify-center overflow-hidden rounded-16 bg-black/20 p-4">
            <ViduLogo2 className="-right-3 -bottom-3 absolute h-[70px] w-[85px] [&_g]:opacity-[0.05]" />
            <div className="relative z-10 flex flex-col gap-2">
              <div className="whitespace-nowrap font-medium text-sm text-system-white leading-5">
                {t.rich('payments.plans.creditsProvide', {
                  credits: () => (
                    <span className={`${CREDITS_CONFIG_LIST[planType].bgImg} mx-1 bg-clip-text text-transparent`}>
                      {CREDITS_CONFIG_LIST[planType].credits}
                    </span>
                  ),
                })}
              </div>

              <CreditsProgressBar
                planType={planType}
                progress={getCreditsProgress(planType)}
                className="w-full flex-none"
              />

              {!isUltimate && (
                <div className="text-system-white48 text-xs leading-[18px]">
                  {t.rich('payments.plans.creditsGenerate', {
                    videos: () => (
                      <span className={`${CREDITS_CONFIG_LIST[planType].bgImg} mx-1 bg-clip-text text-transparent`}>
                        {CREDITS_CONFIG_LIST[planType].videos}
                      </span>
                    ),
                  })}
                  <CustomTooltip
                    content={t('payments.plans.creditsGenerateTips')}
                    trigger="auto"
                    disableHoverableContent={true}
                    container={document.body}
                  >
                    <BaseLineExclamation className="ml-1 inline shrink-0 align-text-bottom text-sm text-system-white/50" />
                  </CustomTooltip>
                </div>
              )}

              {isUltimate && (
                <div className="flex items-center gap-2 text-xs leading-5">
                  <StarIcon className="size-[14px] shrink-0 text-[#9B93FF]" />
                  <span className="bg-[linear-gradient(90deg,#D7DAFF_0%,#8E96FF_86.51%)] bg-clip-text text-transparent">
                    {t.rich('payments.plans.hightPriority', {
                      bold: (chunks) => <span className="font-semibold">{chunks}</span>,
                    })}
                  </span>
                  <CustomTooltip
                    content={t('payments.plans.staggerTipsApplicableModels')}
                    trigger="auto"
                    disableHoverableContent={true}
                    contentClassName="max-w-[260px] p-3"
                    container={document.body}
                  >
                    <BaseLineExclamation className="size-4 shrink-0 cursor-pointer text-[#9B93FF]" />
                  </CustomTooltip>
                </div>
              )}

              {extra_grant_credit > 0 && bonusCode && (
                <div className="flex items-center gap-1 text-sm text-system-white">
                  达人码专属
                  <span className={`${CREDITS_CONFIG_LIST[planType].bgImg} bg-clip-text text-transparent`}>
                    {extra_grant_credit}
                  </span>
                  积分
                </div>
              )}
            </div>
          </div>
        )}

        {/* 高级权益 */}
        {highlightedRights.length > 0 && (
          <div className="mb-3 flex flex-col gap-3 text-left text-sm text-system-white">
            {highlightedRights.map((right) => {
              if (right === 'noWatermark' && SITE === 'china') return null;
              return (
                <div key={right} className="flex items-center gap-2">
                  <BaseFillIntelligentDuration className="size-5 shrink-0 text-[#9B93FF]" />
                  <span className="flex flex-1 items-center font-normal">
                    {(() => {
                      if (right === 'q2ImageUnlimited') {
                        return (
                          <>
                            {t.rich(`payments.plans.${right}`, { referenceNumber: REFERENCE_RIGHT_LIST[planType] })}
                            <CustomTooltip
                              content={t(`payments.plans.${right}_tooltip`)}
                              trigger="auto"
                              disableHoverableContent={true}
                              container={document.body}
                            >
                              <BaseLineExclamation className="ml-1 size-4 shrink-0 cursor-pointer text-system-white40" />
                            </CustomTooltip>
                          </>
                        );
                      }
                      if (right === 'creditsPurchase') {
                        return t.rich('payments.plans.creditsPurchase', {
                          creditsDiscount: creditsDiscount,
                        });
                      }
                      return t.rich(`payments.plans.${right}`, { referenceNumber: REFERENCE_RIGHT_LIST[planType] });
                    })()}
                  </span>
                  {right === 'q2ImageUnlimited' && (
                    <span className="min-h-[17px] shrink-0 rounded-8 bg-[#5765F2] px-2 text-center font-normal text-white text-xs uppercase leading-[1.4]">
                      {t('payments.plans.unlimited365')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {highlightedRights.length > 0 && commonRights.length > 0 && (
          <div className="mt-2 mb-5 h-px bg-system-white08" />
        )}

        {/* 普通权益 */}
        <div className="flex flex-col gap-3 text-left text-sm text-system-white">
          {commonRights.map((right) => {
            if (right === 'noWatermark' && SITE === 'china') return null;
            return (
              <div key={right} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <BaseLineCheck2 className="size-4 shrink-0 text-[#888888]" />
                  <div className="flex items-center">
                    <span className="font-normal">
                      {(() => {
                        if (right === 'creditsPurchase') {
                          return t.rich('payments.plans.creditsPurchase', {
                            creditsDiscount: creditsDiscount,
                          });
                        }
                        return t.rich(`payments.plans.${right}`, { referenceNumber: REFERENCE_RIGHT_LIST[planType] });
                      })()}
                    </span>
                  </div>
                </div>
                {right === 'encodingFormatRight' && (
                  <div className="ml-6 flex items-center gap-1">
                    <BaseLineExclamation className="size-4 shrink-0 text-system-white40" />
                    <span className="text-sm text-system-white40 leading-5">
                      {t('payments.plans.encodingFormatRightTips')}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
